/**
 * LogoUploadField — dealer logo picker for Test Drive Branding.
 *
 * Accepts drop OR click-to-choose OR URL paste. Client-side validation:
 *   - Types: PNG, JPG, SVG, WebP
 *   - Max input file size: 5 MB (raw)
 *   - Auto-scales raster images to ≤ 512 px on the longer edge and
 *     re-encodes as JPEG (quality 0.85) OR PNG (if alpha detected)
 *     so the final base64 stays under ~200 KB.
 *   - SVG passes through untouched (already vector, no scaling).
 * Stores the result as a `data:` URI in the `logo_url` string field
 * (no schema change needed — the popup module renders both URLs and
 * data URIs the same way via <img src>).
 */
import React, { useCallback, useRef, useState } from "react";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
const MAX_RAW_BYTES = 5 * 1024 * 1024; // 5 MB before scaling
const MAX_ENCODED_BYTES = 200 * 1024; // 200 KB after scaling (data-URI length)
const MAX_EDGE = 512;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error("Failed to read file"));
    fr.readAsDataURL(file);
  });
}

// Downscale a raster image (PNG/JPG/WebP) to <= MAX_EDGE and re-encode
// as JPEG at quality 0.85 (or PNG if the source has transparency).
async function scaleRaster(dataUrl, hasAlpha) {
  const img = new Image();
  img.crossOrigin = "anonymous";
  const loaded = new Promise((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Failed to decode image"));
  });
  img.src = dataUrl;
  await loaded;
  let { width: w, height: h } = img;
  const longest = Math.max(w, h);
  if (longest > MAX_EDGE) {
    const s = MAX_EDGE / longest;
    w = Math.round(w * s);
    h = Math.round(h * s);
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const mime = hasAlpha ? "image/png" : "image/jpeg";
  return canvas.toDataURL(mime, 0.85);
}

// Rough alpha detection — read a few pixels from the corner. Not perfect
// but good enough for logo uploads. If in doubt, we keep alpha.
function detectAlpha(file) {
  return file.type === "image/png" || file.type === "image/webp";
}

export default function LogoUploadField({ value, onChange, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [urlInput, setUrlInput] = useState(
    value && !String(value).startsWith("data:") ? value : ""
  );

  const applyFile = useCallback(
    async (file) => {
      setErr(null);
      if (!file) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setErr("Use PNG, JPG, WebP, or SVG.");
        return;
      }
      if (file.size > MAX_RAW_BYTES) {
        setErr(`Under 5 MB, please. (this file is ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
        return;
      }
      setBusy(true);
      try {
        let dataUrl = await fileToDataUrl(file);
        if (file.type === "image/svg+xml") {
          // SVGs pass through unchanged.
          if (dataUrl.length > MAX_ENCODED_BYTES * 1.4) {
            setErr("SVG is too large after encoding — please simplify it.");
            setBusy(false);
            return;
          }
        } else {
          dataUrl = await scaleRaster(dataUrl, detectAlpha(file));
          // If PNG is still too big, retry as JPEG (loses alpha).
          if (dataUrl.length > MAX_ENCODED_BYTES) {
            dataUrl = await scaleRaster(dataUrl, false);
          }
          if (dataUrl.length > MAX_ENCODED_BYTES) {
            setErr("Even after scaling this image is > 200 KB. Try a smaller logo.");
            setBusy(false);
            return;
          }
        }
        onChange(dataUrl);
      } catch (e) {
        setErr(e.message || "Upload failed.");
      } finally {
        setBusy(false);
      }
    },
    [onChange]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const f = e.dataTransfer?.files?.[0];
      if (f) applyFile(f);
    },
    [applyFile]
  );

  const displaySrc = value && String(value).length > 8 ? value : null;
  const isDataUri = value && String(value).startsWith("data:");

  return (
    <div data-testid={testId} className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-300 rounded-md p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer flex items-center gap-3 transition"
      >
        <div className="w-14 h-14 flex-shrink-0 rounded bg-white border border-gray-200 grid place-items-center overflow-hidden">
          {displaySrc ? (
            <img
              src={displaySrc}
              alt="Logo preview"
              className="max-w-full max-h-full object-contain"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ) : (
            <span className="text-[10px] text-gray-400 text-center leading-tight">No logo</span>
          )}
        </div>
        <div className="flex-1 text-[12px]">
          <div className="font-medium text-gray-700">
            {busy ? "Processing…" : displaySrc ? "Replace logo" : "Upload a logo"}
          </div>
          <div className="text-gray-500">
            Drop or click · PNG / JPG / WebP / SVG · scaled to ≤ 200 KB
          </div>
          {err && <div className="text-red-600 mt-1">{err}</div>}
        </div>
        {displaySrc && (
          <button
            type="button"
            className="text-[11px] text-gray-500 hover:text-red-600 underline"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
              setUrlInput("");
            }}
            data-testid={`${testId}-remove`}
          >
            Remove
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ACCEPTED_TYPES.join(",")}
        onChange={(e) => applyFile(e.target.files?.[0])}
        data-testid={`${testId}-file`}
      />
      <div className="flex items-center gap-2">
        <input
          type="url"
          placeholder="…or paste a hosted logo URL"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onBlur={() => {
            const v = urlInput.trim();
            if (!v) return;
            if (!/^https?:\/\//i.test(v)) {
              setErr("URL must start with http:// or https://");
              return;
            }
            setErr(null);
            onChange(v);
          }}
          className="input flex-1 text-[12px]"
          data-testid={`${testId}-url`}
        />
        {isDataUri && (
          <span className="text-[10px] text-gray-500 whitespace-nowrap">
            {Math.round(String(value).length / 1024)} KB
          </span>
        )}
      </div>
    </div>
  );
}
